import { uniqueId } from 'lodash';
import { useState } from 'react';
import styled from 'styled-components';
import {
  Dimension,
  ORDERED_DIMENSIONS,
  ORDERED_IMPACTS_BY_DIMENSION,
} from '../../../../../../../../common/model/types';
import { bouTextFontStyle } from '../../../../../../generic/content/texts/bou-text/text-style';
import { disableTextSelection } from '../../../../../../generic/utilities/fonts';
import { useImpactChartActiveData, useImpactChartContext, useImpactChartHeights } from '../context';
import { grayBorder, ImpactChartHeights } from '../shared';
import { DimensionBlockLoadingSkeleton } from './dimension-block-loading-skeleton';
import { DimensionRow } from './dimension-row';
import { DimensionImpacts } from './impacts';
import { DimensionBlockProps } from './types';

export function DimensionBlock(props: DimensionBlockProps) {
  const { dimension } = props;
  const { isExpanded, width } = useImpactChartContext();
  const heights = useImpactChartHeights();
  const expanded = isExpanded(dimension);
  const [svgId] = useState(uniqueId());

  const data = useImpactChartActiveData(0);

  if (!data) {
    return <DimensionBlockLoadingSkeleton {...{ ...props, svgId }} />;
  }

  return (
    <Root
      style={{
        height: getHeight(heights, dimension, expanded),
        borderBottom: getBorderBottom(dimension),
      }}
    >
      <svg width={width} height={getFullyOpenedHeight(heights, dimension)} style={{ display: 'block' }}>
        <DimensionRow {...props} svgId={svgId} />
        <DimensionImpacts dimension={dimension} svgId={svgId} />
      </svg>
    </Root>
  );
}

function getFullyOpenedHeight(heights: ImpactChartHeights, dimension: Dimension) {
  const impacts = ORDERED_IMPACTS_BY_DIMENSION[dimension];
  return (
    heights.dimensionRowExpanded +
    heights.impactBlockPaddingTop +
    heights.impactBlockPaddingBottom +
    heights.impactRow * impacts.length +
    heights.impactsSpacing * (impacts.length - 1)
  );
}

function getHeight(heights: ImpactChartHeights, dimension: Dimension, expanded: boolean) {
  if (!expanded) {
    return heights.dimensionRow;
  }
  return getFullyOpenedHeight(heights, dimension);
}

export function getBorderBottom(dimension: Dimension) {
  if (dimension === ORDERED_DIMENSIONS[ORDERED_DIMENSIONS.length - 1]) {
    return `1px solid ${grayBorder}`;
  }
  return undefined;
}

export const Root = styled.div`
  ${disableTextSelection()};
  overflow: hidden;
  transition: height 0.12s ease-in-out;
  svg {
    ${bouTextFontStyle};
    transition: height 0.12s ease-in-out;
    line {
      shape-rendering: crispEdges;
    }
  }
`;
